import React, { useState, useRef, useEffect } from "react";
import { Download, Printer } from "lucide-react";
import * as XLSX from "xlsx";
import PreviewModal from "../components/pages/Users/modal/PreviewModal";

export interface ColumnConfig {
  label: string;
  key: string;
}

interface ExportPrintProps {
  rows: Record<string, any>[];
  columns: ColumnConfig[];
  fileName: string;
}

const ExportPrint: React.FC<ExportPrintProps> = ({ rows, columns, fileName }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [pendingExport, setPendingExport] = useState<"csv" | "xlsx" | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // export handlers
  const handleCSV = () => {
    const header = columns.map((col) => col.label);
    const csvRows = [
      header.join(","),
      ...rows.map((row) =>
        columns.map((col) => JSON.stringify(row[col.key] ?? "")).join(",")
      ),
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileName}.csv`;
    a.click();
  };

  const handleXLSX = () => {
    const exportData = rows.map((row) => {
      const obj: Record<string, any> = {};
      columns.forEach((col) => {
        obj[col.label] = row[col.key];
      });
      return obj;
    });
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  // print handler 
  const handlePrint = () => {
    if (!rows || rows.length === 0) return;

    let html = `<table border="1" style="border-collapse: collapse; width: 100%;">`;
    html += "<thead><tr>";
    columns.forEach(col => {
      html += `<th style="padding:8px; text-align:left;">${col.label}</th>`;
    });
    html += "</tr></thead><tbody>";

    rows.forEach(row => {
      html += "<tr>";
      columns.forEach(col => {
        html += `<td style="padding:8px;">${row[col.key] ?? ""}</td>`;
      });
      html += "</tr>";
    });

    html += "</tbody></table>";

    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Print</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              table { border-collapse: collapse; width: 100%; }
              th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
            </style>
          </head>
          <body>${html}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  };

  // preview confirm
  const handleConfirm = () => {
    if (pendingExport === "csv") handleCSV();
    if (pendingExport === "xlsx") handleXLSX();
    setIsPreviewOpen(false);
    setPendingExport(null);
  };

  return (
    <div className="flex items-center gap-4">
      {/* print button */}
      <button
        onClick={handlePrint}
        className="flex items-center gap-2 px-4 py-2 border rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
      >
        <Printer size={16} /> Print
      </button>

      {/* export dropdown */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsDropdownOpen((prev) => !prev)}
          className="flex items-center gap-2 px-4 py-2 border rounded-lg bg-[#143887] text-white"
        >
          <Download size={16} />
          Export
        </button>

        {isDropdownOpen && (
          <div className="absolute right-0 mt-2 w-44 bg-white border rounded-lg shadow-lg z-50">
            <button
              onClick={() => {
                setIsPreviewOpen(true);
                setPendingExport("csv");
                setIsDropdownOpen(false);
              }}
              className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
            >
              Export as CSV
            </button>
            <button
              onClick={() => {
                setIsPreviewOpen(true);
                setPendingExport("xlsx");
                setIsDropdownOpen(false);
              }}
              className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
            >
              Export as XLSX
            </button>
          </div>
        )}

        {/* preview modal */}
        <PreviewModal
          isOpen={isPreviewOpen}
          setIsOpen={setIsPreviewOpen}
          title="Preview Export"
          columns={columns}
          rows={rows}
          onConfirm={handleConfirm}
          confirmLabel={`Export ${pendingExport?.toUpperCase()}`}
        />
      </div>
    </div>
  );
};

export default ExportPrint;




